import DateFnsUtils from '@date-io/moment';
import { KeyboardTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import React, { useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import ErrorText from '../../ErrorText';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { FormFieldProps } from '../../FormTypes';
import FormField from '../../FormField';
import { plusOneDay, timeWithDate } from 'parent-portal/forms/time-operations';
import moment from 'moment';
import { useWidth } from 'reusableComponents/useWidth';
import { FaClock } from 'react-icons/fa';
import { Colors } from 'library';
import { JobTimes } from 'parent-portal/forms/JobRequestTypes';

export default function JobTimePicker({
    jobTimes,
    jobDate,
    startDate,
    allowPastMidnight = true,
    title,
}: {
    jobTimes: FormFieldProps<JobTimes>;
    jobDate: moment.Moment | null;
    startDate?: moment.Moment | null;
    allowPastMidnight?: boolean;
    title?: string;
}) {
    const { start, end } = jobTimes.value;
    const width = useWidth();

    const [durationIsValid, setDurationValid] = useState(true);
    const [startTimeIsValid, setStartTimeValid] = useState(true);
    const [endTimeIsValid, setEndTimeValid] = useState(true);

    const [startIsNull, setStartIsNull] = useState(false);
    const [endIsNull, setEndIsNull] = useState(false);

    useEffect(() => updateValidStates(jobTimes.value), [jobTimes, startIsNull, endIsNull]);
    useEffect(() => updateFieldValidity(durationIsValid, startTimeIsValid, endTimeIsValid), [
        durationIsValid,
        startTimeIsValid,
        endTimeIsValid,
    ]);

    function updateFieldValidity(durationIsValid: boolean, startTimeIsValid: boolean, endTimeIsValid: boolean) {
        jobTimes.setValid(durationIsValid && startTimeIsValid && endTimeIsValid);
    }

    function updateValidStates({ start, end }: JobTimes) {
        const now = moment();

        const startIsDefined = !startIsNull && start !== null;
        const startIsNotInThePast =
            jobDate?.isValid() ||
            (startIsDefined && timeWithDate(start!, startDate ?? jobDate ?? moment()).isAfter(now));
        setStartTimeValid(startIsDefined && startIsNotInThePast);

        setEndTimeValid(!endIsNull && !!end && endTimeAllowPastMidnight(start as moment.Moment, end));

        const durationIsNotDefined = !start || !end;
        const newDurationIsValid = durationIsNotDefined || durationMeetsMinimum(start!, end!);
        setDurationValid(newDurationIsValid);
    }

    function durationMeetsMinimum(start: moment.Moment, end: moment.Moment) {
        const endTimeAndDate = end.isBefore(start) ? plusOneDay(end) : end;

        return endTimeAndDate.diff(start, 'minutes') + 1 > 30;
    }

    function onStartTimeChanged(time: MaterialUiPickersDate) {
        if (!time || !time.isValid()) {
            setStartIsNull(true);
            return;
        }
        setStartIsNull(false);

        jobTimes.setValue({
            ...jobTimes.value,
            start: time,
        });
    }

    function onEndTimeChanged(time: MaterialUiPickersDate) {
        if (!time || !time.isValid()) {
            setEndIsNull(true);
            return;
        }
        setEndIsNull(false);

        jobTimes.setValue({
            ...jobTimes.value,
            end: time,
        });
    }

    function endTimeAllowPastMidnight(start: moment.Moment, end: moment.Moment) {
        if (!allowPastMidnight) {
            return !end.isBefore(start);
        }
        return true;
    }

    const initialFocusedDateStart = start ?? moment().startOf('day').hour(8);
    const initialFocusedDateEnd = end ?? moment().startOf('day').hour(17);

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Grid container>
                <Grid item style={{ marginRight: width === 'xs' ? 0 : 30, width: width === 'xs' ? '100%' : undefined }}>
                    <FormField title={title}>
                        <KeyboardTimePicker
                            id="start"
                            label={'Start'}
                            placeholder="Start: 08:00 AM"
                            mask="__:__ _M"
                            value={start}
                            onChange={onStartTimeChanged}
                            initialFocusedDate={initialFocusedDateStart}
                            variant="inline"
                            style={{ width: '100%', marginBottom: 0 }}
                            keyboardIcon={<FaClock color={Colors.darkNavy} />}
                        />
                        {!startTimeIsValid && !!start && <ErrorText>Invalid start time</ErrorText>}
                    </FormField>
                </Grid>
                <Grid
                    item
                    style={{
                        marginRight: width === 'xs' ? 0 : 30,
                        width: width === 'xs' ? '100%' : undefined,
                    }}
                >
                    <FormField>
                        <KeyboardTimePicker
                            id="end"
                            label={'End'}
                            placeholder="End: 5:00 PM"
                            mask="__:__ _M"
                            value={end}
                            onChange={onEndTimeChanged}
                            initialFocusedDate={initialFocusedDateEnd}
                            variant="inline"
                            style={{ width: '100%' }}
                            keyboardIcon={<FaClock color={Colors.darkNavy} />}
                        />
                        {!endTimeIsValid && !!end && <ErrorText>Invalid end time</ErrorText>}

                        <Grid container xs={5}>
                            {!durationIsValid && <ErrorText>Duration must be at least 30 minutes</ErrorText>}
                        </Grid>
                    </FormField>
                </Grid>
            </Grid>
        </MuiPickersUtilsProvider>
    );
}
